<template>
  <div class="bg p-3 pb-20 mb-auto h-full overflow-auto bg-gradient-to-b bg-gray-800" id="style-1">
    <h2 class="text-xl font-bold text-indigo-300 mb-4">Admin Logs - Item Transfers</h2>

    <!-- Toolbar -->
    <div class="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between mb-3">
      <div class="text-xs text-gray-400">
        แสดงล่าสุดก่อน | เรียงตามเวลา: <span class="text-indigo-300">timestamp desc</span>
      </div>
      <div class="flex gap-2">
        <input
          v-model.trim="filters.search"
          type="text"
          placeholder="ค้นหา (itemName, senderUid, recipientUid)"
          class="px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-72"
          @keyup.enter="refresh()"
        />
        <button
          @click="refresh"
          class="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-indigo-200 rounded-lg border border-gray-600 text-sm"
        >
          ค้นหา
        </button>
      </div>
    </div>

    <!-- Loading / Empty -->
    <div v-if="loading && logs.length === 0" class="text-gray-400">กำลังโหลดข้อมูล...</div>
    <div v-else-if="!loading && logs.length === 0" class="text-gray-400">ไม่มีข้อมูลการโอน</div>

    <!-- Logs list -->
    <div v-else class="space-y-3">
      <div
        v-for="log in logs"
        :key="log.id"
        class="bg-gray-700 rounded-lg p-4 shadow border border-gray-600 hover:border-indigo-400 transition"
      >
        <div class="flex flex-col md:flex-row md:items-start gap-3 md:gap-4 justify-between">
          <div class="flex-1">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm text-gray-400">{{ formatDate(log.timestamp) }}</span>
              <span
                class="px-2 py-1 text-xs rounded"
                :class="log.flags?.sameIp ? 'bg-red-600 text-white' : 'bg-green-600 text-white'"
              >
                {{ log.flags?.sameIp ? 'Same IP' : 'Different IP' }}
              </span>
            </div>

            <!-- Summary grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
              <div>
                <span class="font-semibold text-gray-300">Item:</span>
                <span class="text-indigo-300"> {{ log.itemName || 'N/A' }} </span>
              </div>
              <div>
                <span class="font-semibold text-gray-300">Type:</span>
                <span class="text-gray-200">{{ log.itemData?.type || '-' }}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-300">Rarity:</span>
                <span class="text-yellow-400">{{ log.itemData?.rarity ?? '-' }}</span>
              </div>
              <div>
                <span class="font-semibold text-gray-300">Weight:</span>
                <span class="text-gray-200">{{ log.itemData?.weight ?? '-' }}</span>
              </div>
            </div>

            <!-- Sender/Recipient -->
            <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div class="bg-gray-800 p-2 rounded">
                <span class="block font-semibold text-gray-300">Sender:</span>
                <div class="text-gray-200 break-all">UID: {{ log.sender?.uid || '-' }}</div>
                <div class="text-gray-400">IP: {{ log.sender?.ip || '-' }}</div>
              </div>
              <div class="bg-gray-800 p-2 rounded">
                <span class="block font-semibold text-gray-300">Recipient:</span>
                <div class="text-gray-200 break-all">UID: {{ log.recipient?.uid || '-' }}</div>
                <div class="text-gray-400">IP: {{ log.recipient?.ip || '-' }}</div>
              </div>
            </div>
          </div>

          <!-- Expand button -->
          <div class="md:self-center">
            <button
              class="px-3 py-2 text-sm rounded-lg border border-gray-600 bg-gray-800 hover:bg-gray-700 text-indigo-200"
              @click="toggleExpand(log.id)"
            >
              {{ expanded.has(log.id) ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด' }}
            </button>
          </div>
        </div>

        <!-- Expanded details -->
        <transition name="fade">
          <div v-if="expanded.has(log.id)" class="mt-4 space-y-4">
            <!-- Basic fields -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
              <div class="bg-gray-800 p-3 rounded border border-gray-700">
                <div class="font-semibold text-gray-300 mb-2">ข้อมูลพื้นฐาน</div>
                <div class="text-gray-300">Required Level: <span class="text-gray-200">{{ log.itemData?.requiredLevel ?? '-' }}</span></div>
                <div class="text-gray-300">Wearable: <span class="text-gray-200">{{ boolText(log.itemData?.wearable) }}</span></div>
                <div class="text-gray-300">Transferable: <span class="text-gray-200">{{ boolText(log.itemData?.transfer) }}</span></div>
                <div class="text-gray-300">Currency: <span class="text-gray-200">{{ log.itemData?.currencyType || '-' }}</span></div>
                <div class="text-gray-300">Price: <span class="text-gray-200">{{ log.itemData?.price ?? '-' }}</span></div>
              </div>

              <div class="bg-gray-800 p-3 rounded border border-gray-700">
                <div class="font-semibold text-gray-300 mb-2">ค่าสเตตัส</div>
                <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-200">
                  <div>STA: {{ log.itemData?.stats?.stamina ?? 0 }}</div>
                  <div>STR: {{ log.itemData?.stats?.str ?? 0 }}</div>
                  <div>INT: {{ log.itemData?.stats?.int ?? 0 }}</div>
                  <div>AGI: {{ log.itemData?.stats?.agi ?? 0 }}</div>
                  <div>LUCK: {{ log.itemData?.stats?.luck ?? 0 }}</div>
                  <div>HP+: {{ log.itemData?.stats?.maxHp ?? 0 }}</div>
                  <div>ATK: {{ log.itemData?.stats?.atk ?? 0 }}</div>
                  <div>EVA: {{ log.itemData?.stats?.evasion ?? 0 }}</div>
                  <div>CRIT: {{ log.itemData?.stats?.critical ?? 0 }}</div>
                  <div>DEF: {{ log.itemData?.stats?.defense ?? 0 }}</div>
                </div>
              </div>

              <div class="bg-gray-800 p-3 rounded border border-gray-700">
                <div class="font-semibold text-gray-300 mb-2">เงื่อนไขพิเศษตามประเภท</div>
                <div v-if="log.itemData?.type === 'อาหาร'" class="text-gray-200 space-y-1">
                  <div>Food: {{ log.itemData?.foodLevel ?? 0 }}</div>
                  <div>Happy: {{ log.itemData?.happinessLevel ?? 0 }}</div>
                  <div>Energy: {{ log.itemData?.energyLevel ?? 0 }}</div>
                </div>
                <div v-else-if="log.itemData?.type === 'รักษา'" class="text-gray-200 space-y-1">
                  <div>HealHP%: {{ log.itemData?.healHpPercentage ?? 0 }}</div>
                  <div>VirusHeal%: {{ log.itemData?.virusHealPercentage ?? 0 }}</div>
                </div>
                <div v-else-if="log.itemData?.type === 'กัปดัก'" class="text-gray-200 space-y-1">
                  <div>Dice: {{ log.itemData?.diceCount ?? 0 }}</div>
                  <div>Damage: {{ log.itemData?.damage ?? 0 }}</div>
                  <div>Hours: {{ log.itemData?.durationHours ?? 0 }}</div>
                  <div>Uses: {{ log.itemData?.useCount ?? 0 }}</div>
                </div>
                <div v-else-if="log.itemData?.type === 'ระเบิด'" class="text-gray-200">
                  Damage: {{ log.itemData?.damage ?? 0 }}
                </div>
                <div v-else-if="log.itemData?.type === 'พาหนะ'" class="text-gray-200">
                  Fuel: {{ log.itemData?.fuelLevel ?? 0 }}
                </div>
                <div v-else-if="log.itemData?.type === 'หนังสือ'" class="text-gray-200">
                  Order: {{ log.itemData?.order ?? 0 }}
                </div>
                <div v-else-if="log.itemData?.type === 'หุ่นยนต์สังหาร'" class="text-gray-200 space-y-1">
                  <div>MaxUpgrade: {{ log.itemData?.maxUpgradeCount ?? 0 }}</div>
                  <div>Deployable: <span class="text-indigo-300">{{ (log.itemData?.deployableLocations || []).join(', ') || '-' }}</span></div>
                </div>
                <div v-else-if="log.itemData?.type === 'อวัยวะจักรกล'" class="text-gray-200">
                  MaxUpgrade: {{ log.itemData?.maxUpgradeCount ?? 0 }}
                </div>
                <div v-else class="text-gray-400">—</div>
              </div>
            </div>

            <!-- Decompose & Upgrade -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div class="bg-gray-800 p-3 rounded border border-gray-700">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-semibold text-gray-300">Decompose List</div>
                  <span class="text-xs text-gray-400">decomposable: {{ boolText(log.itemData?.decomposable) }}</span>
                </div>
                <div v-if="(log.itemData?.decomposeList || []).length" class="space-y-1 text-sm">
                  <div v-for="(d, i) in log.itemData.decomposeList" :key="i" class="flex justify-between">
                    <span class="text-indigo-200">{{ d.itemName }}</span>
                    <span class="text-gray-300">{{ d.chance }}%</span>
                  </div>
                </div>
                <div v-else class="text-gray-500 text-sm">ไม่มีรายการ</div>
              </div>

              <div class="bg-gray-800 p-3 rounded border border-gray-700">
                <div class="flex justify-between items-center mb-2">
                  <div class="font-semibold text-gray-300">Upgrade</div>
                  <span class="text-xs text-gray-400">upgradable: {{ boolText(log.itemData?.upgradable) }}</span>
                </div>

                <!-- สำหรับหุ่นยนต์สังหาร -->
                <div v-if="log.itemData?.type === 'หุ่นยนต์สังหาร'">
                  <div class="text-sm text-gray-200 mb-2">
                    Materials:
                    <span class="text-indigo-200">
                      {{ (log.itemData?.upgradeMaterials || []).map(m => `${m.itemName} x${m.quantity}`).join(', ') || '-' }}
                    </span>
                  </div>
                </div>

                <!-- สำหรับอวัยวะจักรกล -->
                <div v-if="log.itemData?.type === 'อวัยวะจักรกล'">
                  <div class="text-sm text-gray-200 mb-2">
                    Upgradeable Stats:
                    <span class="text-indigo-200">{{ (log.itemData?.upgradeableStats || []).join(', ') || '-' }}</span>
                  </div>
                  <div class="space-y-2">
                    <div
                      v-for="(lvl, idx) in (log.itemData?.upgradeLevels || [])"
                      :key="idx"
                      class="bg-gray-900 p-2 rounded text-sm border border-gray-700"
                    >
                      <div class="flex justify-between">
                        <div class="text-gray-300">Level {{ lvl.level }} → {{ (lvl.level || 0) + 1 }}</div>
                        <div class="text-gray-200">Chance: {{ lvl.chance ?? 0 }}%</div>
                      </div>
                      <div class="text-indigo-200">
                        {{ (lvl.materials || []).map(m => `${m.itemName} x${m.quantity}`).join(', ') || '-' }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ทั่วไป -->
                <div v-if="log.itemData?.upgradable && !['หุ่นยนต์สังหาร','อวัยวะจักรกล'].includes(log.itemData?.type)" class="text-sm text-gray-200">
                  Next: <span class="text-indigo-200">{{ log.itemData?.upgradeTo || '-' }}</span> |
                  Chance: <span class="text-gray-200">{{ log.itemData?.upgradeChance ?? 0 }}%</span>
                </div>
                <div v-if="log.itemData?.description" class="mt-2 text-xs text-gray-400">
                  {{ log.itemData.description }}
                </div>
              </div>
            </div>

            <!-- Raw JSON toggle -->
            <div class="mt-2">
              <button
                class="px-3 py-1.5 text-xs rounded border border-gray-600 bg-gray-800 hover:bg-gray-700 text-gray-300"
                @click="toggleRaw(log.id)"
              >
                {{ rawOpen.has(log.id) ? 'ซ่อน JSON' : 'ดู JSON' }}
              </button>
              <pre v-if="rawOpen.has(log.id)" class="mt-2 bg-black/60 text-green-300 p-3 rounded overflow-auto text-xs max-h-80">
{{ prettyJson(log.itemData) }}
              </pre>
            </div>
          </div>
        </transition>
      </div>

      <!-- Load more -->
      <div class="pt-2">
        <button
          v-if="!noMore"
          :disabled="loadingMore"
          @click="loadMore"
          class="w-full py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-indigo-200 rounded-lg border border-gray-600"
        >
          {{ loadingMore ? 'กำลังโหลด...' : 'โหลดเพิ่มเติม' }}
        </button>
        <div v-else class="text-center text-xs text-gray-500 py-2">ไม่มีข้อมูลเพิ่มเติม</div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { projectFirestore } from '@/firebase/config';

const PAGE_SIZE = 1000;

export default {
  setup() {
    const logs = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);
    const noMore = ref(false);
    const lastDoc = ref(null);
    const expanded = ref(new Set()); // เก็บ id ที่เปิดรายละเอียด
    const rawOpen = ref(new Set());  // เก็บ id ที่เปิด JSON
    const filters = ref({ search: '' });

    const formatDate = (timestamp) => {
      if (!timestamp || !timestamp.toDate) return '-';
      const date = timestamp.toDate();
      return date.toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
    };

    const boolText = (v) => (v ? 'Yes' : 'No');
    const prettyJson = (obj) => JSON.stringify(obj ?? {}, null, 2);

    const baseQuery = () =>
      projectFirestore.collection('adminLogs')
        .where('kind', '==', 'itemTransfer')
        .orderBy('timestamp', 'desc');

    const applySearchFilter = (docs) => {
      const q = filters.value.search.toLowerCase();
      if (!q) return docs;
      return docs.filter(d => {
        const itemName = (d.itemName || '').toLowerCase();
        const s = (d.sender?.uid || '').toLowerCase();
        const r = (d.recipient?.uid || '').toLowerCase();
        return itemName.includes(q) || s.includes(q) || r.includes(q);
      });
    };

    const fetchFirstPage = async () => {
      loading.value = true;
      noMore.value = false;
      lastDoc.value = null;
      logs.value = [];
      try {
        const snap = await baseQuery().limit(PAGE_SIZE).get();
        const rows = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        lastDoc.value = snap.docs[snap.docs.length - 1] || null;
        const filtered = applySearchFilter(rows);
        logs.value = filtered;
        // ถ้าโดนกรองจนเหลือน้อย อาจจะยังมีหน้าให้โหลดต่อ
        noMore.value = snap.size < PAGE_SIZE;
      } catch (e) {
        console.error('fetchFirstPage error:', e);
      } finally {
        loading.value = false;
      }
    };

    const loadMore = async () => {
      if (noMore.value || loadingMore.value) return;
      loadingMore.value = true;
      try {
        let q = baseQuery().limit(PAGE_SIZE);
        if (lastDoc.value) q = q.startAfter(lastDoc.value);
        const snap = await q.get();
        if (snap.empty) {
          noMore.value = true;
          return;
        }
        const rows = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        lastDoc.value = snap.docs[snap.docs.length - 1];

        // ใช้ตัวกรองบนชุดใหม่ แล้วค่อยต่อท้าย
        const filtered = applySearchFilter(rows);
        logs.value = logs.value.concat(filtered);

        if (snap.size < PAGE_SIZE) noMore.value = true;
      } catch (e) {
        console.error('loadMore error:', e);
      } finally {
        loadingMore.value = false;
      }
    };

    const refresh = async () => {
      await fetchFirstPage();
    };

    const toggleExpand = (id) => {
      if (expanded.value.has(id)) expanded.value.delete(id);
      else expanded.value.add(id);
      // force update
      expanded.value = new Set(expanded.value);
    };

    const toggleRaw = (id) => {
      if (rawOpen.value.has(id)) rawOpen.value.delete(id);
      else rawOpen.value.add(id);
      rawOpen.value = new Set(rawOpen.value);
    };

    onMounted(fetchFirstPage);

    return {
      logs,
      loading,
      loadingMore,
      noMore,
      formatDate,
      boolText,
      prettyJson,
      loadMore,
      refresh,
      expanded,
      rawOpen,
      filters,
      toggleExpand,
      toggleRaw
    };
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
